import { ChangeEvent, FC, Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Checkbox,
  Icon,
  Image,
  Input,
  List,
  ListItem,
  Select,
  SelectCard,
  SelectCardCheckBox,
  Text,
  Tooltip
} from '@/client/components'
import { IconName, IconSizes } from '@/client/components/Icon/types'
import { SelectForCalcMaterials } from '@/client/components/SelectForCalcMaterial'
import { CartContext } from '@/client/context/cart'
import {
  useGetMaterial,
  useGetPaperLinerMaterial,
  useGetPetLinerMaterial,
  useGetPlusMaterial,
  useGetStandardMaterial,
  useGetSustainableMaterial,
  useMutatePrices,
  useWindowSize
} from '@/client/hooks'
import { getTemplateFilteredOptions, handleSelectKeyValue } from '@/client/modules/OrderTemplates/orderTemplateUtils'
import { IField, IFieldOption } from '@/client/types'
import { useI18n } from '@/locales'
import * as S from '../styles'
import { FieldType, getCalculatorConstants, SettingsChildProps, Steps } from '../types'

const isLabelmakers = process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'us'

// Minimum "max. roll diameter" (mm) per selected core diameter (mm)
const MIN_ROLL_DIAMETER_BY_CORE: Record<number, number> = {
  25: 100,
  40: 120,
  76: 150
}

// Hard maximum "max. roll diameter" (mm), independent of the core
const MAX_ROLL_DIAMETER_LIMIT = 340

// Minimum roll label dimensions
const MIN_ROLL_WIDTH = isLabelmakers ? 0.75 : 20
const MIN_ROLL_HEIGHT = isLabelmakers ? 0.75 : 20
const MIN_ROLL_DIAMETER = isLabelmakers ? 0.75 : 20

// Maximum sheet label dimensions
const MAX_SHEET_WIDTH = 200
const MAX_SHEET_HEIGHT = 275
const MAX_SHEET_DIAMETER = 200

// Maximum label width / height (mm) for finishing other than digital foil
const MAX_FINISHING_WIDTH = 308
const MAX_FINISHING_HEIGHT = 308

// Minimum quantity for pro / smart artwork service and premium proof service
const MIN_SERVICE_QUANTITY = 10

/**
 *
 * @param fields
 * @param step
 * @param randomText
 * @param initialSelection
 * @param reset
 * @param setResetFalse
 * @param serviceType control whether we want to calculate artwork service etc.
 * @param template a order template that is used to preset values, control editability and visibility
 * @param compactView set to true to show calculator more compact
 * @constructor
 */
export const CalculatorStep: FC<SettingsChildProps> = ({
  fields,
  step,
  randomText,
  initialSelection,
  reset,
  setResetFalse,
  serviceType,
  template,
  compactView
}) => {
  const { locale, defaultLocale } = useRouter()
  const { mutate, isLoading: isPriceLoading } = useMutatePrices(locale)
  const { updateSelection, selection } = useContext(CartContext)
  const [showDropdown, setShowDropdown] = useState({
    state: false,
    id: null,
    top: '115px',
    index: 0,
    closeCurrent: false
  })
  const [selectedMaterialId, setSelectedMaterialId] = useState(0)
  const { data: materialData } = useGetMaterial(selectedMaterialId, locale)
  const { data: plusMaterial } = useGetPlusMaterial()
  const { data: standardMaterial } = useGetStandardMaterial()
  const { data: sustainableMaterial } = useGetSustainableMaterial()
  const { data: paperLinerMaterial } = useGetPaperLinerMaterial()
  const { data: petLinerMaterial } = useGetPetLinerMaterial()
  const vRadioRef = useRef<Array<HTMLDivElement | null>>([])
  const relativeParentRef = useRef<HTMLDivElement | null>(null)
  const optionWrapperSet = useRef<HTMLDivElement | null>(null)
  const [newIndex, setNewIndex] = useState<number>(0)
  const [materialType, setMaterialType] = useState<
    'standard' | 'plus' | 'sustainable' | 'paper_liner' | 'pet_liner' | 'default'
  >('default')
  const [adhesiveType, setAdhesiveType] = useState('1')
  const [showMaterialFilter, setShowMaterialFilter] = useState<boolean>(true)
  const [multiSelectForFinishing, setMultiSelectForFinishing] = useState<boolean>(false)
  const [foilHeading, setFoilHeading] = useState<string | undefined>()
  const t = useI18n()
  const windowSize = useWindowSize()
  const breakpointStringTruncation =
    (windowSize?.width > 991 && windowSize?.width < 1273) || (windowSize?.width > 599 && windowSize?.width < 857)

  const templateFilteredOptions = (field: IField, excludeDisabledOptions: boolean = false) =>
    getTemplateFilteredOptions(field, excludeDisabledOptions, template, isDisabled, handleDisabledSelections)

  //For correctly filtering papers based on standard, sustainable or plus
  const materialOptions = useCallback(
    (materialType: 'standard' | 'sustainable' | 'plus' | 'pet_liner' | 'paper_liner' | 'default'): IFieldOption[] => {
      const paperField = fields.find(({ key }) => key === 'paper')

      let validIds: number[] = paperField?.options.map((item) => +item.id) ?? []

      if (materialType === 'standard' && defaultLocale !== 'us') {
        validIds = standardMaterial
      } else if (materialType === 'sustainable' && defaultLocale !== 'us') {
        validIds = sustainableMaterial
      } else if (materialType === 'plus' && defaultLocale !== 'us') {
        validIds = plusMaterial
      } else if (materialType === 'pet_liner' && defaultLocale === 'us') {
        validIds = petLinerMaterial
      } else if (materialType === 'paper_liner' && defaultLocale === 'us') {
        validIds = paperLinerMaterial
      } else if (materialType === 'default') {
        validIds
      }

      if (!validIds) {
        validIds = paperField?.options?.map((e) => Number(e.id)) || []
      }

      return paperField?.options.filter(({ id }) => validIds.includes(+id)) ?? []
    },
    [fields, standardMaterial, sustainableMaterial, plusMaterial]
  )

  const isDisabled = useCallback(
    (disableList, selectionOverride = selection): boolean => {
      if (!disableList) return false
      return disableList.some(({ key, value }) => selectionOverride[key] === value)
    },
    [selection]
  )

  const labelShapeField = fields.find((f) => f.key === 'label_shape')
  const shapeName = labelShapeField?.options.find((option) => option.id === selection.label_shape)?.key
  // The core_diameter selection only holds the option id, the size in mm is part of the option name ("25 mm")
  const coreDiameterField = fields.find((f) => f.key === 'core_diameter')
  const selectedCoreDiameter = Number(
    String(coreDiameterField?.options.find((option) => option.id === selection.core_diameter)?.name ?? '').replace(
      /[^\d.]/g,
      ''
    )
  )

  const handleDisabledSelections = useCallback(
    (fieldKey: string, optionKey: string) => {
      if (
        fieldKey === 'label_type' &&
        optionKey === 'sheet' &&
        shapeName === 'circular' &&
        +selection.diameter > MAX_SHEET_DIAMETER
      ) {
        return true
      }

      if (
        fieldKey === 'label_type' &&
        optionKey === 'sheet' &&
        shapeName !== 'circular' &&
        (+selection.width > MAX_SHEET_WIDTH || +selection.height > MAX_SHEET_HEIGHT)
      ) {
        return true
      }

      if (
        fieldKey === 'label_type' &&
        optionKey === 'roll' &&
        selection.label_type !== initialSelection['label_type'] &&
        shapeName !== 'circular' &&
        (+selection.width < MIN_ROLL_WIDTH || +selection.height < MIN_ROLL_HEIGHT)
      ) {
        return true
      }

      if (
        fieldKey === 'label_type' &&
        optionKey === 'roll' &&
        selection.label_type !== initialSelection['label_type'] &&
        shapeName === 'circular' &&
        +selection.diameter < MIN_ROLL_DIAMETER
      ) {
        return true
      }

      if (fieldKey === 'assembly' && optionKey === 'labels_on_roll' && selection?.max_roll_diameter > 0) {
        return true
      }

      if (fieldKey === 'assembly' && optionKey === 'max_roll_diameter' && selection?.labels_on_roll > 0) {
        return true
      }

      if (
        fieldKey === 'finishing' &&
        optionKey !== 'no_finishing' &&
        optionKey !== 'digital_foil' &&
        (+selection.width > MAX_FINISHING_WIDTH || +selection.height > MAX_FINISHING_HEIGHT)
      ) {
        return true
      }

      if (optionKey === 'quantity' && (serviceType === 'premium' || serviceType === 'basic')) {
        return true
      }

      if (optionKey === 'sorts' && serviceType === 'premium') {
        return true
      }

      return (
        ((fieldKey === 'finishing' && optionKey === 'blind_embossing') || optionKey === 'relief_embossing') &&
        serviceType === 'premium'
      )
    },
    [selection, shapeName, serviceType]
  )

  // REL-776 related
  // Some fields' available options depend on another field's selection (e.g. varnish options
  // depend on the selected paper). When that happens, the previously selected value can become
  // invalid/disabled without the selection state being updated, so a stale (and wrong) value
  // would be sent to the price API. This finds the first selectable option for a field.
  const getFirstValidOption = useCallback(
    (field: IField, candidateSelection): IFieldOption | undefined =>
      field.options?.find(
        (option) => !isDisabled(option.disabled, candidateSelection) && !handleDisabledSelections(field.key, option.key)
      ),
    [isDisabled, handleDisabledSelections]
  )

  // REL-776 related
  // Walks all select-type fields and, for any field whose currently selected value is no longer
  // a valid option (because another selection changed in the same update), falls back to the
  // first available option instead of keeping the now-invalid value.
  const revalidateSelection = useCallback(
    (candidateSelection) => {
      const correctedSelection = { ...candidateSelection }

      fields.forEach((field) => {
        if (field.type !== 'select-radio' && field.type !== 'select-radio-vertical') return

        const currentValue = correctedSelection[field.key]
        if (currentValue === undefined || currentValue === null) return

        const currentOption = field.options?.find((option) => option.id === currentValue)
        const currentOptionStillValid =
          currentOption &&
          !isDisabled(currentOption.disabled, correctedSelection) &&
          !handleDisabledSelections(field.key, currentOption.key)

        if (currentOptionStillValid) return

        const firstValidOption = getFirstValidOption(field, correctedSelection)

        if (firstValidOption && firstValidOption.id !== currentValue) {
          correctedSelection[field.key] = firstValidOption.id
          if (firstValidOption.relatedField) {
            correctedSelection[firstValidOption.relatedField] = firstValidOption.relatedFieldId
          }
        }
      })

      return correctedSelection
    },
    [fields, isDisabled, handleDisabledSelections, getFirstValidOption]
  )

  const handleChange = useCallback(
    (optionId, value) => {
      const newSelection = {
        ...selection,
        [optionId]: value
      }
      //set the quantity to 10 for pro and smart artwork service, premium proof service
      if (optionId === 'quantity' && serviceType && ['pro', 'smart', 'premium'].includes(serviceType)) {
        newSelection['quantity'] = MIN_SERVICE_QUANTITY
      }
      // REL-865 If we have more than 10 sorts quantity must be at least number of sorts to be able to finish upload
      if (optionId === 'sorts' && serviceType && ['pro', 'smart', 'premium'].includes(serviceType)) {
        newSelection['quantity'] = Math.max(MIN_SERVICE_QUANTITY, Number(newSelection['sorts']) || 0)
      }
      //multiply quantity based on sorts for basic proof service
      if (optionId === 'sorts' && serviceType === 'basic') {
        newSelection['quantity'] = 5 * +newSelection['sorts']
      }
      //keep sorts as 1 for premium proof service
      if (optionId === 'sorts' && serviceType === 'premium') {
        newSelection['sorts'] = 1
      }

      updateSelection(newSelection)
    },
    [selection, updateSelection]
  )

  const handleBlur = useCallback(
    (optionId, value) => {
      mutate({
        ...selection,
        [optionId]: value
      })
    },
    [selection, mutate]
  )

  const labelTypeField = fields.find((f) => f.key === 'label_type')
  const labelTypeName = labelTypeField?.options.find((option) => option.id === selection.label_type)?.key

  const handleSelectLight = (fieldKey, value) => {
    handleSelectKeyValue(fieldKey, value, fields, handleSelect)
  }

  /**
   * Handles the selection.
   * @param optionId - The ID of the option that was selected e.g. "label_type"
   * @param value - The value of the option (basically the ID of the option) that was selected e.g., 53 (sheet label)
   * @param relatedFieldId - The ID of the related field e.g., 90
   * @param relatedField -The key of the related field e.g. "die"
   * @param key - The key of the option selected e.g. "sheet" (not always given)
   */
  const handleSelect = useCallback(
    (optionId, value, relatedFieldId, relatedField, key) => {
      if (key === 'relief_embossing') {
        setFoilHeading(getCorrectFoilName(key))
      } else if (key === 'foil') {
        setFoilHeading(undefined)
      }

      const newSelection = {
        ...selection,
        [optionId]: value,
        // REL-776: Compact view (and templates) render the "paper" field as a native <select>, which goes
        // through this generic handler instead of handleSelectRadioVertical. Mirror the same rule
        // used there: a paper change resets the cut type (die) back to its default (free laser
        // cut) unless the newly selected paper option itself forces a premium cut tool via
        // relatedField/relatedFieldId (set below, and which will override this if relatedField is
        // 'die').
        ...(optionId === 'paper' ? { die: initialSelection.die } : {}),
        [relatedField]: relatedFieldId,
        ...(key === 'no_finishing' || key === 'blind_embossing'
          ? {
              foil: initialSelection['foil']
              //embossing_tool_material: initialSelection['embossing_tool_material'] Previously used to indicate if a premium stamping tool is required.
            }
          : {}),
        // ...(key === 'relief_embossing' ? { embossing_tool_material: initialSelection['embossing_tool_material'] } : {}),Previously used to indicate if a premium stamping tool is required.
        ...(key === 'circular' && +selection.diameter > MAX_SHEET_DIAMETER && labelTypeName === 'sheet'
          ? { label_type: initialSelection['label_type'] }
          : {}),
        ...((key === 'rectangular' || key === 'custom') &&
        (+selection.width > MAX_SHEET_WIDTH || +selection.height > MAX_SHEET_HEIGHT) &&
        labelTypeName === 'sheet'
          ? { label_type: initialSelection['label_type'] }
          : {}),
        ...(key === 'circular' && +selection.diameter < MIN_ROLL_DIAMETER && labelTypeName === 'roll'
          ? { diameter: initialSelection['diameter'] }
          : {}),
        ...((key === 'rectangular' || key === 'custom') && +selection.width < MIN_ROLL_WIDTH && labelTypeName === 'roll'
          ? { width: initialSelection['width'] }
          : {}),
        ...((key === 'rectangular' || key === 'custom') &&
        +selection.height < MIN_ROLL_HEIGHT &&
        labelTypeName === 'roll'
          ? { height: initialSelection['height'] }
          : {})
      }

      const cutTypeField = fields.find((f) => f.key === 'die')
      const validDieForNewSelection = cutTypeField ? getFirstValidOption(cutTypeField, newSelection)?.id : undefined

      // Switch back to the first valid option, here it is laser cut
      if (key === 'no_finishing' && optionId === 'finishing' && validDieForNewSelection !== undefined) {
        newSelection.die = validDieForNewSelection
      }

      // REL-776
      // Compact view uses native <select> dropdowns whose options for a field can change as a
      // side effect of another field's change (e.g. varnish options narrow based on the
      // selected paper). In that case the previously selected value can become invalid without
      // the selection state being updated, so fall back to the first valid option for any
      // affected field instead of sending a stale/invalid value to the price API. The card-based
      // (non-compact) UI already handles this per-field where needed, so it's left untouched.
      const validatedSelection = compactView ? revalidateSelection(newSelection) : newSelection

      //allow multiple selction for spot varnish and foil
      setMultiSelectForFinishing(shouldEnableMultiSelect(key, validatedSelection))
      updateSelection(validatedSelection)
      mutate(validatedSelection)

      optionId === 'adhesive' && setAdhesiveType(key)

      const newLabelTypeName = labelTypeField?.options.find(
        (option) => option.id === validatedSelection.label_type
      )?.key
      //If the adhesive is removible or label_type is sheet set the materialType to default
      if (key === 'sheet' || (optionId === 'adhesive' && key === '2') || newLabelTypeName === 'sheet') {
        setShowMaterialFilter(false)
        setMaterialType('default')
      } else {
        setShowMaterialFilter(true)
      }

      //close material dropdown if it is opened
      if (optionId === 'label_type' || optionId === 'adhesive') {
        showDropdown.state && setShowDropdown({ state: false, id: null, top: '115px', index: 0, closeCurrent: false })
      }
    },
    [mutate, selection, updateSelection, compactView, revalidateSelection, getFirstValidOption]
  )

  const handleSelectRadioVertical = useCallback(
    (optionId, value, relatedFieldId, relatedField, index) => {
      setNewIndex(index)
      setSelectedMaterialId(value)

      // REL-735, REL-624
      // Business rule:
      // The "none varnish" option is not allowed for specific papers
      // (attribute_id: 13, 163, 175, 207, 239, 242, etc). For these papers,
      // a different varnish must be selected by default.
      //
      // To support this, previous DB constraints that restricted paper
      // selection based on varnish were removed.
      //
      // Therefore, whenever the paper changes, the selections for
      // finishing, varnish, and foil are reset to their default values
      // to maintain a valid configuration.

      const finishingField = fields.find((f) => f.key === 'finishing')
      const finishingOption = finishingField?.options.find((item) => item.id === selection.finishing)
      const hasFinishing = finishingOption?.key && finishingOption.key !== 'no_finishing'

      const newSelection = {
        ...selection,
        [optionId]: value,
        // Some papers require a premium cut tool (die), which is enforced via the option's
        // relatedField/relatedFieldId (set below). If the newly selected paper does NOT carry
        // such a constraint, reset the cut type back to its default (free laser cut) instead of
        // leaving a stale premium selection in place from a previously selected paper.
        die: initialSelection.die,
        [relatedField]: relatedFieldId,
        // Always start from the default varnish ("none"). If it is not allowed for the new paper,
        // the varnish useEffect switches to the first allowed varnish once the new fields arrive.
        varnish: initialSelection.varnish,
        finishing: hasFinishing ? initialSelection.finishing : selection.finishing,
        foil: hasFinishing ? initialSelection.foil : selection.foil
      }

      if (hasFinishing) {
        setMultiSelectForFinishing(false)
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection, fields, initialSelection]
  )

  const handleSelectCheckbox = useCallback(
    (optionId, nextFieldId, relatedField, relatedFieldId) => {
      const newSelection = {
        ...selection,
        [optionId]: nextFieldId,
        [relatedField]: selection[relatedField]?.filter((item) => item !== relatedFieldId)
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const handleCheckbox = useCallback(
    (optionId, value) => {
      const newSelection = {
        ...selection,
        [optionId]: selection[optionId]?.includes(value)
          ? selection[optionId]?.filter((item) => item !== value) || []
          : [...(selection[optionId] || []), value]
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const shouldEnableMultiSelect = (key: string, newSelection) =>
    newSelection['finishing'] === 117 && newSelection['foil'] !== initialSelection['foil']

  const handleMultipleSelect = useCallback(
    (checked: boolean, key) => {
      if (key === 'foil' || key === 'digital_foil') {
        return multiSelectForFinishing ? multiSelectForFinishing : checked
      } else {
        return checked
      }
    },
    [multiSelectForFinishing]
  )

  const showField = useCallback(
    (excludes): boolean => {
      if (!excludes) return true
      const isExcluded = excludes.some(({ id, value }) => selection[id] === value)
      return !isExcluded
    },
    [selection]
  )

  const checkCorrectSize = useCallback((value: string, max?: string, min?: string) => {
    const maxSize = Number(max)
    const minSize = Number(min)
    const currentValue = Number(value)

    if (currentValue > maxSize) return maxSize
    if (currentValue < minSize) return minSize
    return currentValue
  }, [])

  const normalizeValue = (value: string, max?: string, min?: string): number => {
    if (value.trim() === '') {
      return Number(min ?? 0)
    }

    const checked = checkCorrectSize(value, max, min)

    return locale === 'us' ? checked : Math.round(checked)
  }

  // For the two assembly options an empty field / 0 means "not used": the customer picks either
  // labels per roll or max. roll diameter, and clearing one is how you switch to the other.
  // Without this the new minimum snaps an emptied field straight back up, which permanently
  // disables the other field.
  const normalizeAssemblyValue = (value: string, key: string, max?: string, min?: string): number => {
    const isAssemblyOption = key === 'max_roll_diameter' || key === 'labels_on_roll'

    if (isAssemblyOption && (value.trim() === '' || Number(value) === 0)) {
      return 0
    }

    return normalizeValue(value, max, min)
  }

  // Extra limits for the assembly fields that the API does not deliver:
  // - max. roll diameter: minimum depends on the selected core, maximum is always 340 mm
  // - labels per roll: can never be more than the ordered quantity
  const getAssemblyLimits = useCallback(
    (optionKey: string, min?: string, max?: string): { min?: string; max?: string } => {
      if (optionKey === 'max_roll_diameter') {
        const minByCore = MIN_ROLL_DIAMETER_BY_CORE[selectedCoreDiameter]

        return {
          min: minByCore ? String(minByCore) : min,
          max: String(Math.min(Number(max) || MAX_ROLL_DIAMETER_LIMIT, MAX_ROLL_DIAMETER_LIMIT))
        }
      }

      // We cannot automatically update labels on roll on the frontend because:
      // Reducing quantity would reduce the labels on roll, but we cannot increase it automatically without being sure the
      // customer wants this. Also, a user could order 5 million labels, and the check would allow entering 5 million
      // labels on roll, which I guess is not possible except you have extreme mini labels. Reducing it during typing
      // can also lead to an extreme low value which also impacts pricing.
      //
      // if (optionKey === 'labels_on_roll') {
      //   const quantity = Number(selection.quantity) || 0
      //
      //   return {
      //     min,
      //     max: quantity > 0 ? String(Math.min(Number(max) || quantity, quantity)) : max
      //   }
      // }

      return { min, max }
    },
    [selectedCoreDiameter, selection.quantity]
  )

  // Keep the assembly values inside their limits when what they depend on changes afterwards
  // (switching to a bigger core, or lowering the ordered quantity)
  useEffect(() => {
    const corrections: Record<string, number> = {}

    ;(['max_roll_diameter', 'labels_on_roll'] as const).forEach((optionKey) => {
      const currentValue = Number(selection[optionKey]) || 0
      if (currentValue <= 0) return

      const { min, max } = getAssemblyLimits(optionKey)
      const corrected = checkCorrectSize(String(currentValue), max, min)

      if (corrected !== currentValue) {
        corrections[optionKey] = corrected
      }
    })

    if (Object.keys(corrections).length === 0) return

    const newSelection = { ...selection, ...corrections }
    updateSelection(newSelection)
    mutate(newSelection)
  }, [selection.core_diameter, selection.quantity])

  // Handles switching between valid cut options.
  // The `disabled` rules on an option can reference any selection key (quantity, width,
  // height, paper, ...), so validity is checked against the whole selection via isDisabled.
  useEffect(() => {
    const cutTypeField = fields.find((f) => f.key === 'die')
    if (!cutTypeField) return

    const currentOption = cutTypeField.options.find((option) => option.id === selection.die)
    if (!currentOption) return

    const currentOptionValid =
      !isDisabled(currentOption.disabled, selection) && !handleDisabledSelections(cutTypeField.key, currentOption.key)

    if (currentOptionValid) return

    const nextValidCutType = getFirstValidOption(cutTypeField, selection)

    if (nextValidCutType && nextValidCutType.id !== selection.die) {
      updateSelection({
        ...selection,
        die: nextValidCutType.id
      })
    }
  }, [fields, selection, isDisabled, handleDisabledSelections, getFirstValidOption, updateSelection])

  // REL-926
  // Business rule:
  // For some special papers, the price API leaves the "none" varnish out of the varnish options in
  // the default calculator and premium proof calculator (for basic proof and standalone artwork
  // service, "none" stays available).
  //
  // When a paper is selected, the varnish is reset to the default ("none") in handleSelectRadioVertical.
  // Once the price API has returned the fields for the new paper, this effect checks whether the
  // selected varnish is still available and allowed; if not, it switches to the first allowed varnish.
  useEffect(() => {
    // Only one CalculatorStep instance should correct the varnish, otherwise every mounted step repeats it
    if (step !== Steps.MATERIAL_AND_ADHESIVE) return

    // While a price request is running, `fields` still belong to the previous selection
    if (isPriceLoading) return

    const varnishField = fields.find((f) => f.key === 'varnish')
    if (!varnishField) return

    if (selection.varnish === undefined || selection.varnish === null) return

    const currentOption = varnishField.options.find((option) => option.id === selection.varnish)
    const currentOptionValid =
      currentOption &&
      !isDisabled(currentOption.disabled, selection) &&
      !handleDisabledSelections(varnishField.key, currentOption.key)

    if (currentOptionValid) return

    const nextValidVarnish = getFirstValidOption(varnishField, selection)

    if (nextValidVarnish && nextValidVarnish.id !== selection.varnish) {
      const newSelection = { ...selection, varnish: nextValidVarnish.id }
      updateSelection(newSelection)
      mutate(newSelection)
    }
  }, [
    step,
    isPriceLoading,
    fields,
    selection,
    isDisabled,
    handleDisabledSelections,
    getFirstValidOption,
    updateSelection,
    mutate
  ])

  const activeDropDown = useCallback(
    (id, index) => {
      const clickedElement = vRadioRef.current[index]
      const childLabelElem = clickedElement?.querySelector('label')
      const parentWrapper = relativeParentRef.current

      if (clickedElement && parentWrapper && childLabelElem) {
        const { top } = clickedElement?.getBoundingClientRect()
        const { top: parentWrapperTop } = parentWrapper.getBoundingClientRect()
        setShowDropdown({
          state: true,
          id: id,
          top: `${top - parentWrapperTop + childLabelElem.clientHeight + 10}px`,
          index: index,
          closeCurrent: true
        })
      }
    },
    [showDropdown]
  )

  const handleMaterialFilterSelect = useCallback(
    (filterType) => {
      setMaterialType(filterType)
      //close material dropdown if it is opened
      showDropdown.state &&
        setShowDropdown({
          state: false,
          id: null,
          top: '115px',
          index: 0,
          closeCurrent: false
        })
    },
    [showDropdown]
  )

  const adhesiveName = useCallback(
    (id: number | undefined): string | undefined =>
      fields.find(({ key }) => key === 'adhesive')?.options.find(({ key }) => key === id?.toString())?.name,
    [fields]
  )

  const truncateString = (str, maxLength) => (str.length > maxLength ? str.slice(0, maxLength) + '' : str)

  const getCorrectFoilName = useCallback(
    (keyString) =>
      fields.find(({ key }) => key === 'finishing')?.options.find(({ key }) => key === keyString?.toString())?.name,
    [fields]
  )

  useEffect(() => {
    fields.forEach((field, index) => {
      if (!field.force_value) return
      const { id, key, value, nextFieldId, relatedFieldId, relatedField } = field.force_value
      const onChangeTypes: Partial<Record<FieldType, () => void>> = {
        ['textarea']: () => handleChange(key, value),
        ['input-number']: () => handleChange(key, value),
        ['range']: () => handleChange(id, value),
        ['select-radio']: () => handleSelect(field.key, value, relatedFieldId, relatedField, key),
        ['select-radio-vertical']: () => handleSelectRadioVertical(field.key, id, relatedFieldId, relatedField, index),
        ['select-card-checkbox']: () => handleSelectCheckbox(field.key, nextFieldId, relatedFieldId, relatedField),
        ['checkbox']: () => handleCheckbox(field.key, id)
      }
      onChangeTypes[field.type]?.()
    })

    if (reset) {
      setResetFalse && setResetFalse()
      setSelectedMaterialId(selection.paper)
      setMaterialType('default')
      setShowMaterialFilter(true)
      setAdhesiveType('1')
      setMultiSelectForFinishing(false)
    }

    if (showDropdown.state) {
      activeDropDown(showDropdown.id, newIndex)
    }
  }, [fields, showDropdown.state, selectedMaterialId, step, reset])

  return (
    <>
      {getCalculatorConstants(serviceType, locale)
        .filter((el) => el.step === step)[0]
        ?.fields.map((subfield, index) => (
          <Fragment key={index}>
            {fields
              .filter((er) => er.key == subfield.key)
              .map((field) => (
                <Fragment key={field?.key}>
                  <>
                    {showField(field?.exclude) && (
                      <S.Block hidden={template?.[field?.key]?.hidden} className={`tour-${field.key}`}>
                        <div>
                          <Text
                            size='lg'
                            family='heading'
                            weight='bold'
                            margin='0 0 0.8rem'
                            color={selection[field?.key] ? 'normal' : 'danger'}
                          >
                            {field.key === 'foil' ? foilHeading ?? field.displayName : field.displayName}
                            {field.hint && (
                              <S.Hint>
                                <Tooltip title={field.hint}>
                                  <Icon name='info' />
                                </Tooltip>
                              </S.Hint>
                            )}
                            {!selection[field?.key] && <Icon name='warning' size='s4' />}
                          </Text>
                        </div>
                        <S.Option>
                          {field.type === 'input-number' && (
                            <>
                              {field?.options?.map(
                                ({ id, name, key, unit, min, max, description, lock, priceVariant }: IFieldOption) => {
                                  const templateMin = template?.[key]?.min ? template?.[field.key]?.min : min
                                  const templateMax = template?.[key]?.max ? template?.[field.key]?.max : max
                                  const { min: calculatedMin, max: calculatedMax } = getAssemblyLimits(
                                    key,
                                    templateMin,
                                    templateMax
                                  )
                                  return (
                                    <Fragment key={key}>
                                      {/* show quantity field except for artwork service calulator. Overwrite min value from the order template if given*/}
                                      {serviceType !== 'smart' && serviceType !== 'pro' && (
                                        <S.CBlock hidden={template?.[key]?.hidden}>
                                          <Input
                                            dimension='large'
                                            type='number'
                                            step={locale === 'us' ? 'any' : 1}
                                            min={calculatedMin}
                                            max={calculatedMax}
                                            key={id}
                                            id={id}
                                            name={name}
                                            description={description}
                                            disabled={
                                              template?.[key]?.disabled ||
                                              lock == 1 ||
                                              handleDisabledSelections(field.key, key)
                                            }
                                            label={`${name} ${unit ?? ''}`}
                                            priceVariant={priceVariant ? `(${priceVariant})` : ''}
                                            value={selection[key]}
                                            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                              handleChange(key, event.currentTarget.value)
                                            }}
                                            onBlur={(event: ChangeEvent<HTMLInputElement>): void => {
                                              const valueFormatted = normalizeAssemblyValue(
                                                event.currentTarget.value,
                                                key,
                                                calculatedMax,
                                                calculatedMin
                                              )

                                              handleBlur(key, valueFormatted)
                                              handleChange(key, valueFormatted)
                                            }}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                              if (event.key === 'Enter') {
                                                const valueFormatted = normalizeValue(
                                                  event.currentTarget.value,
                                                  calculatedMax,
                                                  calculatedMin
                                                )

                                                handleBlur(key, valueFormatted)
                                                handleChange(key, valueFormatted)
                                              }
                                            }}
                                            style={{ width: field.key === 'assembly' ? '22rem' : 'auto' }}
                                          />
                                        </S.CBlock>
                                      )}
                                      {/* hide quantity field for artwork service calculator */}
                                      {(serviceType === 'smart' || serviceType === 'pro') && key !== 'quantity' && (
                                        <S.CBlock>
                                          <Input
                                            dimension='large'
                                            type='number'
                                            min={min}
                                            max={max}
                                            key={id}
                                            id={id}
                                            name={name}
                                            description={description}
                                            disabled={lock == 1 || handleDisabledSelections(field.key, key)}
                                            label={`${name} ${unit ?? ''}`}
                                            value={selection[key]}
                                            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                              handleChange(key, Number(event.currentTarget.value).toFixed())
                                            }}
                                            onBlur={(event: ChangeEvent<HTMLInputElement>): void => {
                                              const { value } = event.currentTarget

                                              const valueFormatted = checkCorrectSize(value, max, min)
                                              handleBlur(key, Number(valueFormatted).toFixed())
                                              handleChange(key, Number(valueFormatted).toFixed())
                                            }}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                              if (event.key === 'Enter') {
                                                const { value } = event.currentTarget

                                                const valueFormatted = checkCorrectSize(value, max, min)
                                                handleBlur(key, Number(valueFormatted).toFixed())
                                                handleChange(key, Number(valueFormatted).toFixed())
                                              }
                                            }}
                                            style={{ width: field.key === 'assembly' ? '16rem' : 'auto' }}
                                          />
                                        </S.CBlock>
                                      )}
                                    </Fragment>
                                  )
                                }
                              )}
                            </>
                          )}

                          {field.type === 'select-radio' && (
                            <>
                              {compactView && (
                                <Select
                                  id={field.key}
                                  name={field.key}
                                  options={templateFilteredOptions(field, true)}
                                  value={String(selection[field.key])}
                                  onChange={(e) => {
                                    // We need to find the other details like relatedField/relatedFieldId
                                    handleSelectLight(field.key, e.target.value)
                                  }}
                                  disabled={template?.[field.key]?.disabled}
                                />
                              )}
                              {!compactView &&
                                field?.options
                                  ?.filter(({ id }) => {
                                    // Related to REL-776 Filter out options that you do not want to see
                                    const valuePositiveList = template?.[field.key]?.value_positive_list

                                    if (!valuePositiveList?.length) {
                                      return true
                                    }

                                    return valuePositiveList.map(String).includes(String(id))
                                  })
                                  .map(
                                    ({
                                      id,
                                      name,
                                      icon,
                                      description,
                                      disabled,
                                      lock,
                                      key,
                                      warning,
                                      relatedFieldId,
                                      relatedField,
                                      priceVariant
                                    }: IFieldOption) => (
                                      <SelectCard
                                        key={id}
                                        id={id}
                                        value={id}
                                        name={field.key}
                                        label={name}
                                        description={description}
                                        warning={warning}
                                        disabled={
                                          template?.[field.key]?.disabled ||
                                          handleDisabledSelections(field.key, key) ||
                                          lock == 1 ||
                                          isDisabled(disabled)
                                        }
                                        checked={id === selection[field.key]}
                                        onChange={() => handleSelect(field.key, id, relatedFieldId, relatedField, key)}
                                        iconName={icon}
                                        priceVariant={priceVariant ? `(${priceVariant})` : ''}
                                      />
                                    )
                                  )}
                            </>
                          )}

                          {field.type === 'select-radio-vertical' && field.key === 'paper' && (
                            <S.VerticalRadioOption>
                              {/* Material filtering section */}
                              {showMaterialFilter && !compactView && (
                                <S.FilterMaterial>
                                  <S.Filter
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => handleMaterialFilterSelect('default')}
                                    onKeyPress={() => handleMaterialFilterSelect('default')}
                                    clicked={materialType === 'default'}
                                  >
                                    <Text weight='bold' margin='0.25rem'>
                                      {randomText?.all_materials}
                                    </Text>
                                  </S.Filter>
                                  {defaultLocale !== 'us' && (
                                    <>
                                      <S.Filter
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => handleMaterialFilterSelect('standard')}
                                        onKeyPress={() => handleMaterialFilterSelect('standard')}
                                        clicked={materialType === 'standard'}
                                      >
                                        <Text weight='bold' margin='0.25rem'>
                                          {randomText?.standard}
                                        </Text>
                                      </S.Filter>
                                      <S.Filter
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => handleMaterialFilterSelect('plus')}
                                        onKeyPress={() => handleMaterialFilterSelect('plus')}
                                        clicked={materialType === 'plus'}
                                      >
                                        <S.Hint>
                                          <Tooltip title={t('PLUS_MATERIAL_HINT')}>
                                            <Icon name='info' />
                                          </Tooltip>
                                        </S.Hint>

                                        <Text weight='bold' margin='0.25rem'>
                                          {randomText?.plus_material}
                                        </Text>
                                      </S.Filter>
                                      <S.Filter
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => handleMaterialFilterSelect('sustainable')}
                                        onKeyPress={() => handleMaterialFilterSelect('sustainable')}
                                        clicked={materialType === 'sustainable'}
                                      >
                                        <Text weight='bold' color='olive-green' margin='0.25rem'>
                                          {randomText?.sustainable}
                                        </Text>
                                      </S.Filter>
                                    </>
                                  )}
                                  {/* Following filters are only available in us shop */}
                                  {defaultLocale === 'us' && (
                                    <>
                                      <S.Filter
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => handleMaterialFilterSelect('paper_liner')}
                                        onKeyPress={() => handleMaterialFilterSelect('paper_liner')}
                                        clicked={materialType === 'paper_liner'}
                                      >
                                        <Text weight='bold' margin='0.25rem'>
                                          Paper Liner
                                        </Text>
                                      </S.Filter>
                                      <S.Filter
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => handleMaterialFilterSelect('pet_liner')}
                                        onKeyPress={() => handleMaterialFilterSelect('pet_liner')}
                                        clicked={materialType === 'pet_liner'}
                                      >
                                        <Text weight='bold' margin='0.25rem'>
                                          PET Liner
                                        </Text>
                                      </S.Filter>
                                    </>
                                  )}
                                </S.FilterMaterial>
                              )}

                              {compactView && (
                                <Select
                                  id={field.key}
                                  name={field.key}
                                  options={templateFilteredOptions(field, true)}
                                  value={String(selection[field.key])}
                                  onChange={(e) => {
                                    // We need to find the other details like relatedField/relatedFieldId
                                    handleSelectLight(field.key, e.target.value)
                                  }}
                                  disabled={template?.[field.key]?.disabled}
                                />
                              )}
                              {!compactView && (
                                <S.Option className='v-option-wrapper' ref={relativeParentRef}>
                                  {materialOptions(materialType)
                                    .filter(({ id }) => {
                                      // Related to REL-776 Filter out options that you do not want to see
                                      const valuePositiveList = template?.[field.key]?.value_positive_list

                                      if (!valuePositiveList?.length) {
                                        return true
                                      }

                                      return valuePositiveList.map(String).includes(String(id))
                                    })
                                    .map(
                                      (
                                        {
                                          id,
                                          name,
                                          description,
                                          disabled,
                                          lock,
                                          relatedFieldId,
                                          relatedField
                                        }: IFieldOption,
                                        index
                                      ) => (
                                        <div
                                          key={id}
                                          className={
                                            showDropdown.state && +id === showDropdown.id
                                              ? 'v-select-card dropdown-active'
                                              : 'v-select-card'
                                          }
                                          ref={(ref) => {
                                            vRadioRef.current[index] = ref
                                          }}
                                        >
                                          <SelectForCalcMaterials
                                            key={id}
                                            id={id}
                                            value={id}
                                            name={field.key}
                                            label={truncateString(name, breakpointStringTruncation ? 21 : 34)}
                                            description={description}
                                            disabled={
                                              template?.[field.key]?.disabled || lock == 1 || isDisabled(disabled)
                                            }
                                            checked={id === selection[field.key]}
                                            openDropdown={showDropdown.state && +id === showDropdown.id}
                                            setOpenDropdown={setShowDropdown}
                                            onChange={() =>
                                              handleSelectRadioVertical(
                                                field.key,
                                                id,
                                                relatedFieldId,
                                                relatedField,
                                                index
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    )}
                                  {/* Material dropdown section */}
                                  {showDropdown.state && +selection['paper'] === showDropdown.id && (
                                    <S.OptionDropdown style={{ top: showDropdown.top }} ref={optionWrapperSet}>
                                      {/* Material API */}
                                      <>
                                        <S.MaterialImage>
                                          <Image src={materialData?.image_url} alt='paper' width={165} height={220} />
                                        </S.MaterialImage>
                                        <div>
                                          <Text color='weak' margin='1rem 0 0 1rem' padding='0'>
                                            Material
                                          </Text>
                                          <Text weight='bold' margin='0 0 0.5rem 1rem'>
                                            {materialData?.name}
                                          </Text>
                                          <S.MaterialDetails>
                                            <S.MaterialDescription>
                                              <>
                                                <List kind='gold'>
                                                  <ListItem
                                                    key='key1'
                                                    iconName={'liquid-resistance' as IconName}
                                                    iconSize={'s3' as IconSizes}
                                                    weight={'normal'}
                                                    border={'bottom'}
                                                  >
                                                    {Array.from(
                                                      { length: materialData?.liquid_resistance as number },
                                                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                      (_) => (
                                                        <S.IconAligner key={`filled-${index}`}>
                                                          <Icon name='circle-filled' size='s2' />
                                                        </S.IconAligner>
                                                      )
                                                    )}
                                                    {Array.from(
                                                      { length: 3 - (materialData?.liquid_resistance as number) },
                                                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                      (_) => (
                                                        <S.IconAligner key={`unfilled-${index}`}>
                                                          <Icon name='circle-unfilled' size='s2' />
                                                        </S.IconAligner>
                                                      )
                                                    )}
                                                  </ListItem>
                                                  <ListItem
                                                    key='key2'
                                                    iconName={'new-durability' as IconName}
                                                    iconSize={'s3' as IconSizes}
                                                    weight={'normal'}
                                                    border={'bottom'}
                                                  >
                                                    <Text margin='0'>
                                                      {materialData?.min_temperature}
                                                      {defaultLocale === 'us' ? ' °F ' : ' °C '}
                                                    </Text>
                                                    <Image
                                                      src={`/icons/temperature.svg`}
                                                      alt='temperature'
                                                      width={50}
                                                      height={20}
                                                    />
                                                    <Text margin='0'>
                                                      {materialData?.max_temperature}
                                                      {defaultLocale === 'us' ? ' °F ' : ' °C '}
                                                    </Text>
                                                  </ListItem>
                                                  <ListItem
                                                    key='key3'
                                                    iconName={'new-permanent' as IconName}
                                                    iconSize={'s3' as IconSizes}
                                                    weight={'normal'}
                                                    border={'bottom'}
                                                  >
                                                    <Text margin='0'>
                                                      {!adhesiveName(+adhesiveType)
                                                        ? ' - '
                                                        : adhesiveName(+adhesiveType)}
                                                    </Text>
                                                  </ListItem>
                                                  <ListItem
                                                    key='key4'
                                                    iconName={'recycle' as IconName}
                                                    iconSize={'s3' as IconSizes}
                                                    weight={'normal'}
                                                    border={'bottom'}
                                                  >
                                                    <S.IconAligner>
                                                      <Icon name='eco' />
                                                    </S.IconAligner>
                                                    {locale !== 'au' && materialData?.certificate && (
                                                      <>
                                                        <S.IconAligner>
                                                          <Icon name='fsc' />
                                                        </S.IconAligner>
                                                        <Text margin='0'>{materialData?.certificate} </Text>
                                                      </>
                                                    )}
                                                  </ListItem>
                                                  <S.IconDetails>
                                                    <Icon name='liquid-resistance' size='s2' />{' '}
                                                    {randomText?.liquid_resistance}
                                                    {'   '}
                                                    <Icon name='new-durability' size='s2' />
                                                    {'   '}
                                                    {randomText?.durability}
                                                    {'   '} <Icon name='new-permanent' size='s2' />
                                                    {randomText?.adhesive}
                                                  </S.IconDetails>
                                                </List>
                                              </>
                                            </S.MaterialDescription>
                                            <S.MaterialRecommendation>
                                              <Text margin='0' color='weak'>
                                                {randomText?.recommendation}
                                              </Text>
                                              <Text lineHeight='1.4'>
                                                {!materialData?.recommendation ? '-' : materialData?.recommendation}
                                              </Text>
                                            </S.MaterialRecommendation>
                                          </S.MaterialDetails>
                                        </div>
                                      </>
                                    </S.OptionDropdown>
                                  )}
                                </S.Option>
                              )}
                            </S.VerticalRadioOption>
                          )}

                          {field.type === 'select-radio-vertical' && field.key === 'finishing' && (
                            <>
                              {compactView && (
                                <Select
                                  id={field.key}
                                  name={field.key}
                                  options={templateFilteredOptions(field, true)}
                                  value={selection[field.key]}
                                  onChange={(e) => {
                                    // We need to find the other details like relatedField/relatedFieldId
                                    handleSelectLight(field.key, e.target.value)
                                  }}
                                  disabled={template?.[field.key]?.disabled}
                                />
                              )}
                              {!compactView && (
                                <S.Option className='finishing-option' ref={relativeParentRef}>
                                  {field?.options?.map(
                                    ({
                                      id,
                                      name,
                                      icon,
                                      disabled,
                                      lock,
                                      warning,
                                      description,
                                      key,
                                      relatedFieldId,
                                      relatedField
                                    }: IFieldOption) => (
                                      <SelectCard
                                        key={id}
                                        orientation='vertical'
                                        id={id}
                                        value={id}
                                        name={field.key}
                                        label={name}
                                        warning={warning}
                                        description={description}
                                        disabled={
                                          handleDisabledSelections(field.key, key) ||
                                          lock == 1 ||
                                          isDisabled(disabled) ||
                                          template?.[field.key]?.disabled
                                        }
                                        checked={handleMultipleSelect(id === selection[field.key], key)}
                                        onChange={() => handleSelect(field.key, id, relatedFieldId, relatedField, key)}
                                        giveBackground={`/img/${icon}.png`}
                                        iconName={icon}
                                        textClassName='finishing-text'
                                        checkbox={true}
                                      />
                                    )
                                  )}
                                </S.Option>
                              )}
                            </>
                          )}

                          {field.type === 'select-radio-vertical' &&
                            field.key !== 'paper' &&
                            field.key !== 'finishing' && (
                              <div style={{ borderWidth: '1px' }}>
                                {compactView && (
                                  <Select
                                    id={field.key}
                                    name={field.key}
                                    options={templateFilteredOptions(field, true)}
                                    value={selection[field.key]}
                                    onChange={(e) => {
                                      // We need to find the other details like relatedField/relatedFieldId
                                      handleSelectLight(field.key, e.target.value)
                                    }}
                                    disabled={template?.[field.key]?.disabled}
                                  />
                                )}
                                {!compactView && (
                                  <S.Option className='v-option-wrapper' ref={relativeParentRef}>
                                    {field.options.map(
                                      ({
                                        id,
                                        name,
                                        description,
                                        icon,
                                        disabled,
                                        lock,
                                        warning,
                                        key,
                                        relatedFieldId,
                                        relatedField
                                      }: IFieldOption) => (
                                        <SelectCard
                                          key={id}
                                          orientation='vertical'
                                          id={id}
                                          value={id}
                                          name={field.key}
                                          label={name}
                                          description={description}
                                          warning={warning}
                                          disabled={
                                            template?.[field.key]?.disabled || lock == 1 || isDisabled(disabled)
                                          }
                                          checked={id === selection[field.key]}
                                          onChange={() =>
                                            handleSelect(field.key, id, relatedFieldId, relatedField, key)
                                          }
                                          iconName={icon}
                                        />
                                      )
                                    )}
                                  </S.Option>
                                )}
                              </div>
                            )}

                          {field.type === 'select-card-checkbox' && (
                            <>
                              {field?.options?.map(
                                ({
                                  id,
                                  name,
                                  description,
                                  key,
                                  nextFieldId,
                                  relatedFieldId,
                                  relatedField,
                                  icon
                                }: IFieldOption) => (
                                  <SelectCardCheckBox
                                    key={id}
                                    id={id}
                                    value={id}
                                    name={field.key}
                                    label={name}
                                    description={description}
                                    checked={key !== 'false'}
                                    disabled={template?.[field.key]?.disabled}
                                    kind='circle'
                                    subText=''
                                    rightEndText=''
                                    boxType='withIcon'
                                    iconName={icon}
                                    compact
                                    onChange={() =>
                                      handleSelectCheckbox(field.key, nextFieldId, relatedField, relatedFieldId)
                                    }
                                  />
                                )
                              )}
                            </>
                          )}

                          {field.type === 'checkbox' && (
                            <>
                              {field?.options?.map(({ id, name, description }: IFieldOption) => (
                                <Checkbox
                                  key={id}
                                  id={id}
                                  value={id}
                                  name={field.key}
                                  label={name}
                                  description={description}
                                  checked={selection[field.key].includes(id)}
                                  disabled={template?.[field.key]?.disabled}
                                  kind='square'
                                  onChange={() => handleCheckbox(field.key, id)}
                                />
                              ))}
                            </>
                          )}
                        </S.Option>

                        {field.error_message && (
                          <Text margin='0.8rem 0 0' color='danger' size='sm'>
                            {field.error_message}
                          </Text>
                        )}
                      </S.Block>
                    )}
                  </>
                </Fragment>
              ))}
          </Fragment>
        ))}
    </>
  )
}
